import React from "react";

/**
 * Allow Starbeam to set IS_RESTRICTED to true in situations (such as the
 * lifecycle constructor), thare sometimes rendering and sometimes not (and
 * therefore should always be treated as readonly to prevent writes from
 * occurring).
 */
let IS_RESTRICTED = false;

export function beginReadonly(): void {
  IS_RESTRICTED = true;
}

export function endReadonly(): void {
  IS_RESTRICTED = false;
}

/**
 * If the current active render reads from a reactive value, and the reactive
 * value is already set up to notify React in the context of another frame
 * (that will **reliably** update as the same rate as the this read), then we
 * can avoid the "untracked read" error by wrapping the consumption in
 * {@linkcode unsafeTrackedElsewhere} and {@linkcode
 * endUnsafeTrackedElsewhere}.
 */
let IS_UNRESTRICTED = false;

/**
 * This function is used in the fundamental `@starbeam/react` building blocks
 * (such as `useReactive`) that glue Starbeam reactive values into React's
 * rendering. These building blocks directly subscribe to changes in reactive
 * values (and unsubscribe when the component is unmounted), but they also need
 * to return the current value of the reactive without triggering the
 * "untracked read" error.
 *
 * The functionality in this file **does not** change the semantics or behavior
 * of the reactive system, but rather allows `@starbeam/react` to produce a
 * user-friendly error message when the user attempts to read from a reactive
 * value outside of a component (which wouldn't update reliably).
 *
 * User code (and higher-level functions of `@starbeam/react`) should not use
 * this function directly.
 */
export function unsafeTrackedElsewhere<T>(callback: () => T): T {
  const current = IS_UNRESTRICTED;
  IS_UNRESTRICTED = true;

  try {
    return callback();
  } finally {
    IS_UNRESTRICTED = current;
  }
}

/**
 * This function is used to wrap the setup function of a component, so that it
 * can read from reactive values without triggering the "untracked read" error.
 */
export function setupFunction<T>(callback: () => T): T {
  const current = IS_UNRESTRICTED;
  IS_UNRESTRICTED = true;

  try {
    return callback();
  } finally {
    IS_UNRESTRICTED = current;
  }
}

/**
 * Returns true if reading from a reactive value is currently disallowed
 * (because React is currently rendering a component, and we're not in an
 * unrestricted context, such as setup).
 *
 * React 19 renamed the private internals object from
 * `__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED` to
 * `__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE`, and
 * replaced the `ReactCurrentOwner.current` field with the top-level `H`
 * dispatcher.
 *
 * Detecting "am I rendering?" from `H`: `H` is set to various dispatchers
 * during render, and set to `ContextOnlyDispatcher` outside of render
 * (whose hook methods all throw). `H === null` means no React context at
 * all. A real render dispatcher has distinct functions per hook; the
 * throwing dispatcher shares a single `throwInvalidHookError` function
 * across every hook method. Comparing `useState === useMemo` distinguishes
 * the two cheaply.
 *
 * The React team may tighten access further, so we wrap the lookup
 * defensively and fall back to `IS_RESTRICTED` if the shape ever drifts.
 */
export function isRestrictedRead(): boolean {
  if (IS_UNRESTRICTED) {
    return false;
  }

  return isReactRendering() || IS_RESTRICTED;
}

function isReactRendering(): boolean {
  try {
    const internals = (
      React as unknown as {
        __CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE?: {
          H?: {
            useState?: unknown;
            useMemo?: unknown;
          } | null;
        };
      }
    ).__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE;

    const dispatcher = internals?.H;
    if (dispatcher == null) return false;

    // ContextOnlyDispatcher shares throwInvalidHookError across all hook
    // methods; a real render dispatcher has distinct functions per hook.
    return dispatcher.useState !== dispatcher.useMemo;
  } catch {
    return false;
  }
}
